import { db, auth } from '../../config/firebase';
import {
    collection,
    doc,
    getDocs,
    getDoc,
    setDoc,
    updateDoc,
    deleteDoc,
    query,
    where
} from 'firebase/firestore';

const COLLECTION_NAME = 'departments';

/**
 * Get current user ID from Firebase Auth
 */
const getCurrentUserId = () => {
    return auth.currentUser?.uid || 'system';
};

/**
 * Get all departments with optional filters
 */
export const getDepartments = async (filters = {}) => {
    try {
        let q = query(
            collection(db, COLLECTION_NAME),
            where('isDeleted', '==', false)
        );

        const snapshot = await getDocs(q);
        const rawDepartments = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));

        // Get employee counts for all raw departments first
        const [employeesSnapshot, usersSnapshot] = await Promise.all([
            getDocs(collection(db, 'employees')),
            getDocs(collection(db, 'users'))
        ]);
        
        const allProfiles = [
            ...employeesSnapshot.docs.map(doc => ({ ...doc.data(), collection: 'employees' })),
            ...usersSnapshot.docs.map(doc => ({ ...doc.data(), collection: 'users' }))
        ].filter(p => {
            if (p.isDeleted === true) return false;
            const isInactive = (p.status || '').toLowerCase() === 'inactive' || p.isActive === false;
            return !isInactive;
        });
        
        const departmentsWithCounts = rawDepartments.map(dept => {
            const deptCode = (dept.code || '').toUpperCase().trim();
            const deptName = (dept.name || '').toUpperCase().trim();
            
            const seenEmails = new Set();
            const memberCount = allProfiles.filter(profile => {
                const profileDept = (
                    profile.job?.department || 
                    profile.jobDetails?.department || 
                    profile.department || 
                    ''
                ).toUpperCase().trim();
                
                const matches = (profileDept === deptCode || profileDept === deptName);
                if (matches) {
                    const email = (profile.email || profile.personal?.email || profile.personalInfo?.email || '').toLowerCase();
                    if (email && seenEmails.has(email)) return false;
                    if (email) seenEmails.add(email);
                    return true;
                }
                return false;
            }).length;

            return { ...dept, employeeCount: memberCount };
        });

        // Function to build a nested hierarchy
        const buildHierarchy = (items, parentId = '') => {
            return items
                .filter(item => (item.parentId || '') === parentId)
                .map(item => ({
                    ...item,
                    children: buildHierarchy(items, item.code)
                }))
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        };

        // If hierarchy is requested via filters or by default
        if (filters.hierarchical) {
            return buildHierarchy(departmentsWithCounts);
        }

        // Apply search/status filters for flat list if hierarchy not requested
        let filtered = departmentsWithCounts;
        if (filters.status) filtered = filtered.filter(d => d.status === filters.status);
        if (filters.search) {
            const s = filters.search.toLowerCase();
            filtered = filtered.filter(d => d.code?.toLowerCase().includes(s) || d.name?.toLowerCase().includes(s));
        }

        return filtered;
    } catch (error) {
        console.error('Error fetching departments:', error);
        throw error;
    }
};

/**
 * Get department by ID
 */
export const getDepartmentById = async (id) => {
    try {
        const docRef = doc(db, COLLECTION_NAME, id);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists() || docSnap.data().isDeleted) {
            return null;
        }

        const department = { id: docSnap.id, ...docSnap.data() };

        // Get employee count
        const employeesSnapshot = await getDocs(
            query(
                collection(db, 'employees'),
                where('department', '==', department.code),
                where('isDeleted', '==', false)
            )
        );
        department.employeeCount = employeesSnapshot.size;

        return department;
    } catch (error) {
        console.error('Error fetching department:', error);
        throw error;
    }
};

/**
 * Create new department
 */
export const createDepartment = async (data, userId) => {
    try {
        if (!data.code || !data.name) {
            throw new Error('Department code and name are required');
        }

        const code = data.code.toUpperCase().trim();
        const name = data.name.trim();

        if (code.length < 2 || code.length > 10) {
            throw new Error('Department code must be 2-10 characters');
        }
        if (!/^[A-Z0-9]+$/.test(code)) {
            throw new Error('Department code must be alphanumeric');
        }
        if (name.length < 2 || name.length > 100) {
            throw new Error('Department name must be 2-100 characters');
        }

        const existingDoc = await getDoc(doc(db, COLLECTION_NAME, code));
        if (existingDoc.exists() && !existingDoc.data().isDeleted) {
            throw new Error('Department code already exists');
        }

        // Use provided userId or get from auth
        const currentUserId = userId || getCurrentUserId();

        const department = {
            code,
            name,
            description: data.description?.trim() || '',
            parentId: data.parentId || '',
            status: 'active',
            createdAt: new Date(),
            createdBy: currentUserId,
            updatedAt: new Date(),
            updatedBy: currentUserId,
            isDeleted: false
        };

        await setDoc(doc(db, COLLECTION_NAME, code), department);
        return { id: code, ...department };
    } catch (error) {
        console.error('Error creating department:', error);
        throw error;
    }
};

/**
 * Update department
 */
export const updateDepartment = async (id, data, userId) => {
    try {
        const docRef = doc(db, COLLECTION_NAME, id);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists() || docSnap.data().isDeleted) {
            throw new Error('Department not found');
        }

        // Use provided userId or get from auth
        const currentUserId = userId || getCurrentUserId();
        const updates = { updatedAt: new Date(), updatedBy: currentUserId };

        if (data.name !== undefined) {
            const name = data.name.trim();
            if (name.length < 2 || name.length > 100) {
                throw new Error('Department name must be 2-100 characters');
            }
            updates.name = name;
        }
        if (data.description !== undefined) {
            updates.description = data.description.trim();
        }
        if (data.parentId !== undefined) {
            if (data.parentId === id) throw new Error('A department cannot be its own parent');
            updates.parentId = data.parentId;
        }

        await updateDoc(docRef, updates);
        const updated = await getDoc(docRef);
        return { id: updated.id, ...updated.data() };
    } catch (error) {
        console.error('Error updating department:', error);
        throw error;
    }
};

/**
 * Delete department
 */
export const deleteDepartment = async (id) => {
    try {
        const docRef = doc(db, COLLECTION_NAME, id);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists() || docSnap.data().isDeleted) {
            throw new Error('Department not found');
        }

        // Check for sub-departments
        const subDeptsSnapshot = await getDocs(
            query(
                collection(db, COLLECTION_NAME),
                where('parentId', '==', id),
                where('isDeleted', '==', false)
            )
        );

        if (subDeptsSnapshot.size > 0) {
            throw new Error(`Cannot delete department that has ${subDeptsSnapshot.size} sub-department(s). Delete them first.`);
        }

        const department = docSnap.data();
        const employeesSnapshot = await getDocs(
            query(
                collection(db, 'employees'),
                where('department', '==', department.code),
                where('isDeleted', '==', false)
            )
        );

        if (employeesSnapshot.size > 0) {
            throw new Error(`Cannot delete department with ${employeesSnapshot.size} assigned employees.`);
        }

        await deleteDoc(docRef);
    } catch (error) {
        console.error('Error deleting department:', error);
        throw error;
    }
};

/**
 * Update department status
 */
export const updateDepartmentStatus = async (id, status, userId) => {
    try {
        if (!['active', 'inactive'].includes(status)) {
            throw new Error('Invalid status');
        }

        const docRef = doc(db, COLLECTION_NAME, id);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists() || docSnap.data().isDeleted) {
            throw new Error('Department not found');
        }

        // Use provided userId or get from auth
        const currentUserId = userId || getCurrentUserId();

        await updateDoc(docRef, { status, updatedAt: new Date(), updatedBy: currentUserId });
        const updated = await getDoc(docRef);
        return { id: updated.id, ...updated.data() };
    } catch (error) {
        console.error('Error updating department status:', error);
        throw error;
    }
};

/**
 * Get employees in department
 */
export const getDepartmentEmployees = async (departmentCode, filters = {}) => {
    try {
        const snapshot = await getDocs(
            query(
                collection(db, 'employees'),
                where('department', '==', departmentCode),
                where('isDeleted', '==', false)
            )
        );

        let employees = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
                id: doc.id,
                name: data.personal?.fullName || '',
                employeeCode: data.job?.employeeCode || '',
                designation: data.job?.designation || '',
                status: data.status || ''
            };
        });

        employees.sort((a, b) => a.name.localeCompare(b.name));

        const page = parseInt(filters.page) || 1;
        const limit = parseInt(filters.limit) || 20;
        const total = employees.length;
        const startIndex = (page - 1) * limit;

        return {
            employees: employees.slice(startIndex, startIndex + limit),
            pagination: { page, limit, total, totalPages: Math.ceil(total / limit) }
        };
    } catch (error) {
        console.error('Error fetching department employees:', error);
        throw error;
    }
};
